import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2, ClipboardList, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import api from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const TYPES_PROJET = [
  { v: "site_vitrine", label: "Site vitrine professionnel" },
  { v: "ecommerce", label: "Site e-commerce" },
  { v: "app_web", label: "Application web sur mesure" },
  { v: "app_mobile", label: "Application mobile (iOS/Android)" },
  { v: "automatisation", label: "Automatisation / Chatbot IA / CRM" },
  { v: "refonte", label: "Refonte d'un site existant" },
  { v: "autre", label: "Autre / Plusieurs" },
];
const SECTEURS = ["Restauration", "Coiffure / Beauté", "Immigration", "Immobilier", "Santé / Clinique", "Commerce", "Services pros", "Industriel", "Autre"];
const BUDGETS = ["Moins de 5 000 $", "5 000 $ – 10 000 $", "10 000 $ – 25 000 $", "25 000 $ – 50 000 $", "50 000 $ +", "À discuter"];
const DELAIS = ["Urgent (< 1 mois)", "1 à 2 mois", "2 à 4 mois", "4 à 6 mois", "Flexible"];

const empty = {
  nom: "", entreprise: "", poste: "", courriel: "", telephone: "", site_actuel: "",
  secteur: "", taille_equipe: "", clientele: "",
  type_projet: "", objectifs: "", problemes_resoudre: "", fonctionnalites_souhaitees: "",
  inspirations: "", contenu_disponible: "",
  public_cible: "", concurrents: "",
  integrations: "", multilingue: "non", seo_requis: "oui",
  budget: "", delai: "",
  commentaires: "",
};

const STEPS = [
  { id: 1, label: "Vous", fields: ["nom", "entreprise", "poste", "courriel", "telephone", "site_actuel"] },
  { id: 2, label: "Entreprise", fields: ["secteur", "taille_equipe", "clientele"] },
  { id: 3, label: "Projet", fields: ["type_projet", "objectifs", "problemes_resoudre", "fonctionnalites_souhaitees", "inspirations", "contenu_disponible"] },
  { id: 4, label: "Cible & technique", fields: ["public_cible", "concurrents", "integrations", "multilingue", "seo_requis"] },
  { id: 5, label: "Budget & calendrier", fields: ["budget", "delai", "commentaires"] },
];

const REQUIRED = ["nom", "entreprise", "courriel", "secteur", "type_projet", "objectifs", "problemes_resoudre", "budget", "delai"];

export default function ProjectBrief() {
  const [form, setForm] = useState(empty);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const progress = (step / STEPS.length) * 100;

  const validateStep = () => {
    const stepFields = STEPS[step - 1].fields;
    for (const f of stepFields) {
      if (REQUIRED.includes(f) && !form[f]) {
        toast.error("Veuillez remplir les champs obligatoires.");
        return false;
      }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(STEPS.length, s + 1)); };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    for (const r of REQUIRED) {
      if (!form[r]) { toast.error("Champs obligatoires manquants."); return; }
    }
    setLoading(true);
    try {
      await api.post("/project-brief", form);
      setDone(true);
      toast.success("Cahier des charges envoyé ! Confirmation par courriel.");
    } catch {
      toast.error("Erreur d'envoi. Réessayez.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="project-brief-page">
      <Seo
        title="Cahier des charges — Démarrer votre projet"
        description="Décrivez votre projet en 5 étapes. Recevez une proposition personnalisée sous 24h pour votre site web, application ou automatisation IA."
        path="/cahier-des-charges"
      />

      <section className="relative bg-[#0B1F3A] text-white pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-dot opacity-25" />
        <div className="absolute -top-32 right-1/4 h-[400px] w-[400px] rounded-full bg-[#00B8D9]/20 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-6 md:px-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00B8D9]/15 border border-[#00B8D9]/30 text-[#00B8D9] text-xs font-bold tracking-[0.18em] uppercase mb-5">
            <ClipboardList className="h-3.5 w-3.5" />
            Cahier des charges · 5 minutes
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-balance">
            Démarrez votre <span className="text-[#00B8D9]">projet</span>
          </h1>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl">
            Donnez-nous le contexte de votre projet. Nous revenons sous <b>24 heures</b> avec une proposition personnalisée et un devis détaillé.
          </p>
        </div>
      </section>

      <section className="bg-[#F5F7FA] py-16">
        <div className="mx-auto max-w-3xl px-6 md:px-10">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-10">
            {done ? (
              <div className="text-center py-10" data-testid="brief-success">
                <CheckCircle2 className="h-16 w-16 text-[#00B8D9] mx-auto mb-4" />
                <h2 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">Cahier des charges reçu !</h2>
                <p className="text-[#4B5563] max-w-md mx-auto">
                  Merci {form.nom}. Notre équipe analyse votre projet et vous reviendra sous <b>24 h ouvrables</b> avec une proposition personnalisée.
                </p>
                <p className="text-[#4B5563] mt-2 text-sm">Une confirmation a été envoyée à <b>{form.courriel}</b>.</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <span className="text-[#4B5563]">Étape {step} / {STEPS.length} — <b className="text-[#0B1F3A]">{STEPS[step - 1].label}</b></span>
                    <span className="text-[#00B8D9] font-bold">{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>

                <AnimatePresence mode="wait">
                  <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                    {step === 1 && (
                      <>
                        <h3 className="font-heading text-xl font-bold text-[#0B1F3A]">Parlez-nous de vous</h3>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div><Label>Nom complet *</Label><Input data-testid="brief-nom" value={form.nom} onChange={(e) => set("nom", e.target.value)} /></div>
                          <div><Label>Entreprise *</Label><Input data-testid="brief-entreprise" value={form.entreprise} onChange={(e) => set("entreprise", e.target.value)} /></div>
                          <div><Label>Poste / Fonction</Label><Input value={form.poste} onChange={(e) => set("poste", e.target.value)} /></div>
                          <div><Label>Téléphone</Label><Input value={form.telephone} onChange={(e) => set("telephone", e.target.value)} /></div>
                          <div><Label>Courriel *</Label><Input data-testid="brief-courriel" type="email" value={form.courriel} onChange={(e) => set("courriel", e.target.value)} /></div>
                          <div><Label>Site web actuel (URL)</Label><Input placeholder="https://" value={form.site_actuel} onChange={(e) => set("site_actuel", e.target.value)} /></div>
                        </div>
                      </>
                    )}

                    {step === 2 && (
                      <>
                        <h3 className="font-heading text-xl font-bold text-[#0B1F3A]">Votre entreprise</h3>
                        <div>
                          <Label>Secteur d'activité *</Label>
                          <Select value={form.secteur} onValueChange={(v) => set("secteur", v)}>
                            <SelectTrigger data-testid="brief-secteur"><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                            <SelectContent>{SECTEURS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Taille de l'équipe</Label><Input placeholder="Ex: 5 employés" value={form.taille_equipe} onChange={(e) => set("taille_equipe", e.target.value)} /></div>
                        <div><Label>Décrivez votre clientèle actuelle</Label><Textarea rows={3} placeholder="Profil type, zone géographique..." value={form.clientele} onChange={(e) => set("clientele", e.target.value)} /></div>
                      </>
                    )}

                    {step === 3 && (
                      <>
                        <h3 className="font-heading text-xl font-bold text-[#0B1F3A]">Votre projet</h3>
                        <div>
                          <Label>Type de projet *</Label>
                          <Select value={form.type_projet} onValueChange={(v) => set("type_projet", v)}>
                            <SelectTrigger data-testid="brief-type"><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                            <SelectContent>{TYPES_PROJET.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Objectifs principaux *</Label><Textarea data-testid="brief-objectifs" rows={3} placeholder="Ex: doubler les prises de RDV en ligne, vendre 24/7..." value={form.objectifs} onChange={(e) => set("objectifs", e.target.value)} /></div>
                        <div><Label>Problèmes à résoudre *</Label><Textarea data-testid="brief-problemes" rows={3} placeholder="Quels sont les points de douleur que ce projet doit régler ?" value={form.problemes_resoudre} onChange={(e) => set("problemes_resoudre", e.target.value)} /></div>
                        <div><Label>Fonctionnalités souhaitées</Label><Textarea rows={3} placeholder="Ex: paiement Stripe, formulaire multi-étapes, espace client, blog, chatbot IA..." value={form.fonctionnalites_souhaitees} onChange={(e) => set("fonctionnalites_souhaitees", e.target.value)} /></div>
                        <div><Label>Inspirations / sites que vous aimez</Label><Input placeholder="URLs séparées par des virgules" value={form.inspirations} onChange={(e) => set("inspirations", e.target.value)} /></div>
                        <div><Label>Contenu disponible</Label><Input placeholder="Textes, photos, logo... déjà prêts ? À créer ?" value={form.contenu_disponible} onChange={(e) => set("contenu_disponible", e.target.value)} /></div>
                      </>
                    )}

                    {step === 4 && (
                      <>
                        <h3 className="font-heading text-xl font-bold text-[#0B1F3A]">Cible et technique</h3>
                        <div><Label>Public cible</Label><Textarea rows={2} placeholder="Décrivez votre client idéal (âge, besoin, comportement...)" value={form.public_cible} onChange={(e) => set("public_cible", e.target.value)} /></div>
                        <div><Label>Concurrents</Label><Input placeholder="Noms / URLs des concurrents directs" value={form.concurrents} onChange={(e) => set("concurrents", e.target.value)} /></div>
                        <div><Label>Intégrations nécessaires</Label><Input placeholder="Ex: Stripe, HubSpot, Calendly, Mailchimp, QuickBooks..." value={form.integrations} onChange={(e) => set("integrations", e.target.value)} /></div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <Label>Multilingue ?</Label>
                            <RadioGroup value={form.multilingue} onValueChange={(v) => set("multilingue", v)} className="flex gap-3 mt-2">
                              <label className={`flex items-center gap-2 px-3 py-2 border-2 rounded-lg cursor-pointer ${form.multilingue === "non" ? "border-[#00B8D9] bg-[#00B8D9]/5" : "border-gray-200"}`}>
                                <RadioGroupItem value="non" /> Non
                              </label>
                              <label className={`flex items-center gap-2 px-3 py-2 border-2 rounded-lg cursor-pointer ${form.multilingue === "fr-en" ? "border-[#00B8D9] bg-[#00B8D9]/5" : "border-gray-200"}`}>
                                <RadioGroupItem value="fr-en" /> FR + EN
                              </label>
                              <label className={`flex items-center gap-2 px-3 py-2 border-2 rounded-lg cursor-pointer ${form.multilingue === "plus" ? "border-[#00B8D9] bg-[#00B8D9]/5" : "border-gray-200"}`}>
                                <RadioGroupItem value="plus" /> 3+
                              </label>
                            </RadioGroup>
                          </div>
                          <div>
                            <Label>SEO important ?</Label>
                            <RadioGroup value={form.seo_requis} onValueChange={(v) => set("seo_requis", v)} className="flex gap-3 mt-2">
                              <label className={`flex items-center gap-2 px-3 py-2 border-2 rounded-lg cursor-pointer ${form.seo_requis === "oui" ? "border-[#00B8D9] bg-[#00B8D9]/5" : "border-gray-200"}`}>
                                <RadioGroupItem value="oui" /> Oui, prioritaire
                              </label>
                              <label className={`flex items-center gap-2 px-3 py-2 border-2 rounded-lg cursor-pointer ${form.seo_requis === "moyen" ? "border-[#00B8D9] bg-[#00B8D9]/5" : "border-gray-200"}`}>
                                <RadioGroupItem value="moyen" /> Moyen
                              </label>
                              <label className={`flex items-center gap-2 px-3 py-2 border-2 rounded-lg cursor-pointer ${form.seo_requis === "non" ? "border-[#00B8D9] bg-[#00B8D9]/5" : "border-gray-200"}`}>
                                <RadioGroupItem value="non" /> Non
                              </label>
                            </RadioGroup>
                          </div>
                        </div>
                      </>
                    )}

                    {step === 5 && (
                      <>
                        <h3 className="font-heading text-xl font-bold text-[#0B1F3A]">Budget & calendrier</h3>
                        <div>
                          <Label>Budget estimé *</Label>
                          <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                            <SelectTrigger data-testid="brief-budget"><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                            <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Délai souhaité *</Label>
                          <Select value={form.delai} onValueChange={(v) => set("delai", v)}>
                            <SelectTrigger data-testid="brief-delai"><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                            <SelectContent>{DELAIS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Commentaires libres</Label><Textarea rows={4} placeholder="Tout ce que vous voulez ajouter..." value={form.commentaires} onChange={(e) => set("commentaires", e.target.value)} /></div>

                        <div className="bg-[#0B1F3A]/5 border border-[#00B8D9]/20 rounded-lg p-4 mt-4">
                          <div className="flex gap-3">
                            <Sparkles className="h-5 w-5 text-[#00B8D9] flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-[#4B5563]">Une fois soumis, vous recevrez une <b className="text-[#0B1F3A]">confirmation par courriel</b> et notre équipe vous contactera sous 24 h avec une proposition personnalisée.</p>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="flex gap-2 pt-4 border-t border-gray-200 mt-6">
                      {step > 1 && <Button variant="outline" onClick={back} data-testid="brief-back">Retour</Button>}
                      <div className="flex-1" />
                      {step < STEPS.length ? (
                        <Button onClick={next} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="brief-next">
                          Continuer <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      ) : (
                        <Button onClick={submit} disabled={loading} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="brief-submit">
                          {loading ? "Envoi..." : "Envoyer le cahier des charges"}
                        </Button>
                      )}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
